import { getChatCompletion } from '@/lib/ai/chatCompletion';

export interface LeadClassificationResult {
  priority_score: number;       // 1–10 (10 = highest urgency)
  urgency_level: 'low' | 'medium' | 'high' | 'critical';
  property_type_flag: string;   // e.g. "landlord_management", "tenant_rights", "hmo", "general"
  classification_summary: string; // 1-sentence human-readable rationale
}

const SYSTEM_PROMPT = `You are a lead classification assistant for PDA Lettings, a UK lettings agency in Chester.
Your job is to analyse incoming enquiry leads and score them for urgency and property type so the team can prioritise responses.

Scoring rules:
- priority_score: integer 1–10. 10 = most urgent (e.g. eviction, emergency repair, legal issue). 1 = least urgent (general info request).
- urgency_level: "critical" (score 9-10), "high" (score 7-8), "medium" (score 4-6), "low" (score 1-3).
- property_type_flag: one of "landlord_management", "landlord_valuation", "landlord_compliance", "tenant_rights", "tenant_repair", "hmo", "general".
- classification_summary: one concise sentence (max 20 words) explaining the priority decision.

Respond ONLY with valid JSON matching this schema exactly:
{
  "priority_score": <number 1-10>,
  "urgency_level": "<low|medium|high|critical>",
  "property_type_flag": "<string>",
  "classification_summary": "<string>"
}`;

export async function classifyLead(
  fullName: string,
  inquiryType: string,
  pageSource: string
): Promise<LeadClassificationResult | null> {
  try {
    const userMessage = `Lead details:
- Name: ${fullName}
- Enquiry type: ${inquiryType}
- Page source: ${pageSource}

Classify this lead.`;

    const response = await getChatCompletion(
      'GEMINI',
      'gemini/gemini-2.5-flash',
      [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: userMessage },
      ],
      {
        temperature: 0.1,
        max_tokens: 200,
        response_format: { type: 'json_object' },
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content) return null;

    const parsed = JSON.parse(content) as LeadClassificationResult;

    // Validate required fields
    if (
      typeof parsed.priority_score !== 'number' ||
      !parsed.urgency_level ||
      !parsed.property_type_flag ||
      !parsed.classification_summary
    ) {
      return null;
    }

    return parsed;
  } catch {
    // Classification is non-blocking — fail silently
    return null;
  }
}
